/**
 * Logo — official 3mote brand assets.
 *
 * Two surfaces:
 *   - `<LogoMark />`     — just the rounded-square icon (red/yellow/blue
 *                          Kanban-column bars). Use this in tight chrome
 *                          like the collapsed sidebar, favicons, OG image
 *                          fallbacks.
 *   - `<LogoWordmark />` — mark + "3MOTE" wordmark, with the wordmark
 *                          sized at ~75% of the mark's height per
 *                          David's tuned spec. Use this in wider chrome
 *                          like the expanded sidebar header, marketing
 *                          site nav, login page.
 *
 * Vector geometry comes directly from the official SVG David shipped
 * (rev 3 — frame rx=60 generous corners, bars rx=22 moderately rounded
 * ends; landed after iterating through rev 1 (bars rx=31) and rev 2
 * (frame rx=48, bars rx=14)):
 *   - Frame: 308×308, rx=60.
 *   - Three bars: x ∈ {36, 123, 210}, y=33, w=62, h=242, rx=22.
 *   - Colors: #ED230D / #FCB827 / #00A0FE.
 *
 * `tone="dark"` (default) renders the frame in a fixed black — the same
 * black `public/favicon.svg` uses — so the mark is theme-independent and
 * never repaints with the surrounding chrome. It was `currentColor`
 * until the frame turned near-white on the Obsidian / Midnight themes,
 * where the chrome's own text colour is light. `tone="light"` flips the
 * frame to white for use on a dark background. Bar colors are always the
 * brand triad. (The wordmark text still follows `currentColor` on
 * `tone="dark"` — it is type, and reads as chrome.)
 *
 * The wordmark uses Rubik Bold to match the official charactermark; the
 * font is loaded via `src/styles/global.css`. System-bold is the
 * fallback while the webfont loads.
 */

import type { CSSProperties } from 'react'

const RED = '#ED230D'
const YELLOW = '#FCB827'
const BLUE = '#00A0FE'
// Frame black is fixed rather than `currentColor`: the mark sits in
// chrome whose text colour is near-white on the dark themes, which drew
// a white frame there. This is the same black `public/favicon.svg` uses.
const BLACK = '#000000'

interface LogoMarkProps {
  /** Pixel size of the square mark. Default 32. */
  size?: number
  /**
   * `dark` (default): fixed black frame, theme-independent — matches the
   * favicon. `light`: white frame, for a dark background.
   */
  tone?: 'dark' | 'light'
  className?: string
  style?: CSSProperties
  /** Hidden from screen readers when true (decorative). Default false; pair with explicit aria-label. */
  decorative?: boolean
}

export function LogoMark({
  size = 32,
  tone = 'dark',
  className,
  style,
  decorative = false,
}: LogoMarkProps) {
  const frameFill = tone === 'dark' ? BLACK : '#FFFFFF'
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 308 308"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : '3mote'}
      className={className}
      style={style}
    >
      {!decorative && <title>3mote</title>}
      <rect width="308" height="308" rx="60" ry="60" fill={frameFill} />
      <rect x="36" y="33" width="62" height="242" rx="22" ry="22" fill={RED} />
      <rect x="123" y="33" width="62" height="242" rx="22" ry="22" fill={YELLOW} />
      <rect x="210" y="33" width="62" height="242" rx="22" ry="22" fill={BLUE} />
    </svg>
  )
}

interface LogoWordmarkProps extends LogoMarkProps {
  /** Wordmark text-fill override. Defaults to currentColor (inherits chrome). */
  textFill?: string
}

/**
 * Wordmark layout — viewBox is 308 high (matches the mark). The 3MOTE
 * text uses `font-size: 300` so the visible cap-height (~72% of font-size
 * for Rubik Bold) lands around 216 ≈ 70% of the 308 mark height. David
 * tuned this in across multiple revisions:
 *   - 90% (font-size 396) — too tall, text overshot the mark
 *   - 50% (text-[18px] HTML fallback) — too small
 *   - 75% (font-size 330) — close, but text felt slightly oversized
 *   - 70% (font-size 300) — landed here.
 *
 * The text baseline is anchored via `dominant-baseline="central"` + y=154
 * (mark midline) so the text optically centers on the mark.
 *
 * Rubik Bold "3MOTE" at font-size 300 measures ≈ 945 units wide
 * (3.15em × 300 ≈ 945, minus letter-spacing of −5×4 = −20, ≈ 925).
 * Mark adds 308 + 20 gap = 328 left margin (gap reduced from 32 → 20
 * per David's "smidge closer" directive). Total content reach ≈ 1253.
 * viewBoxWidth = 1280 leaves a hair of right padding so SVG
 * `overflow: hidden` doesn't clip the trailing E.
 */
export function LogoWordmark({
  size = 36,
  tone = 'dark',
  className,
  style,
  decorative = false,
  textFill,
}: LogoWordmarkProps) {
  const frameFill = tone === 'dark' ? BLACK : '#FFFFFF'
  const resolvedTextFill = textFill ?? (tone === 'dark' ? 'currentColor' : '#FFFFFF')
  // Aspect ≈ 4.16:1 with the wordmark fully fitted at font-size 300.
  const viewBoxWidth = 1280
  const renderedWidth = size * (viewBoxWidth / 308)
  return (
    <svg
      width={renderedWidth}
      height={size}
      viewBox={`0 0 ${viewBoxWidth} 308`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : '3mote'}
      className={className}
      style={style}
    >
      {!decorative && <title>3mote</title>}
      {/* Mark — same geometry as `<LogoMark>` */}
      <rect width="308" height="308" rx="60" ry="60" fill={frameFill} />
      <rect x="36" y="33" width="62" height="242" rx="22" ry="22" fill={RED} />
      <rect x="123" y="33" width="62" height="242" rx="22" ry="22" fill={YELLOW} />
      <rect x="210" y="33" width="62" height="242" rx="22" ry="22" fill={BLUE} />
      {/*
       * Wordmark — Rubik Bold.
       *
       * x = 308 (mark right edge) + 32 (gap) = 340.
       * y = 154 (mark vertical midline). `dominant-baseline="central"`
       *     vertically centers the optical glyph height.
       * font-size = 396 → cap-height ≈ 277 ≈ 90% of 308 mark height.
       */}
      <text
        x="328"
        y="154"
        fill={resolvedTextFill}
        fontFamily="'Rubik', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif"
        fontWeight={700}
        fontSize="300"
        letterSpacing="-5"
        dominantBaseline="central"
      >
        3MOTE
      </text>
    </svg>
  )
}
